// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Class<T extends object> = new (...args: any[]) => T;
type MethodExecutionGuardOptions = { checkCondition?: () => boolean; onCheckFail?: string | VoidFunction };

/**
 * This decorator is used to validate checkCondition() and execute the wrapped class methods when the condition is met
 * or execute onCheckFail() or throw an error if it is not met. You can pass the error to throw to the decorator.
 *
 * @param options - The options for the decorator.
 * @param options.checkCondition - The condition to check. By default, it checks if the condition is false.
 * @param options.onCheckFail - The error message to throw or the action to execute when the condition is not met.
 * @returns The decorated class.
 */
export const MethodExecutionGuard =
  ({ checkCondition = () => false, onCheckFail }: MethodExecutionGuardOptions) =>
  <T extends object>(constructor: Class<T>): Class<T> => {
    if (typeof constructor === 'function' && typeof constructor.prototype === 'object')
      return new Proxy(constructor, {
        // The constructor function of the class.
        construct(target, args) {
          const instance = new target(...args);
          return new Proxy(instance, {
            get(targetInstance, propertyKey) {
              // @ts-expect-error Getting property
              const propertyValue = targetInstance[propertyKey];
              if (typeof propertyValue === 'function' && propertyKey !== 'constructor') {
                return function (...methodArgs: unknown[]) {
                  try {
                    if (checkCondition()) {
                      return propertyValue.apply(targetInstance, methodArgs);
                    }
                    switch (typeof onCheckFail) {
                      case 'string':
                        // Throw an error with the message from onCheckFail
                        throw new Error(onCheckFail);
                      case 'function':
                        // Execute the function from onCheckFail
                        return onCheckFail?.();
                      default:
                        break;
                    }
                  } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error(error);
                  }
                };
              }
              // Return the property value
              return propertyValue;
            },
          });
        },
      });
    // Return the constructor as is
    return constructor;
  };
